import { Layers, Workflow, Database, Store, Sparkles, Save, Server, Plug, Download, Globe, Github, Rocket, Image as ImageIcon, ArrowRight, Share2, GitBranch } from "lucide-react";
import CodeBlock from "./CodeBlock";

const methodTone = (m) => {
  const x = (m || "GET").toUpperCase();
  if (x === "GET") return "bg-teal/15 text-teal";
  if (x === "POST") return "bg-saffron/20 text-marigold";
  if (x === "PUT" || x === "PATCH") return "bg-blue-100 text-blue-700";
  if (x === "DELETE") return "bg-red-100 text-red-700";
  return "bg-paper text-ink2";
};

export default function BlueprintCard({ bp, onSave, saving, saved, onExport, onWebPreview, onPushGitHub, onShare, onBranch }) {
  if (!bp) return null;
  return (
    <article className="space-y-6 animate-fade-up" data-testid="blueprint-card">
      <header className="card-soft p-7">
        <div className="label-eyebrow mb-2">App Blueprint</div>
        <h2 className="font-heading text-3xl md:text-4xl font-semibold mb-3 leading-tight" data-testid="blueprint-title">
          {bp.title}
        </h2>
        <p className="text-ink2 leading-relaxed">{bp.concept}</p>
        <div className="mt-5 flex flex-wrap gap-3">
          {onExport && (
            <button onClick={onExport} className="btn-primary text-sm py-2" data-testid="blueprint-export-button">
              <Download className="w-4 h-4" /> Download starter kit
            </button>
          )}
          {onWebPreview && (
            <button
              onClick={onWebPreview}
              className="btn-outline-ink text-sm py-2"
              data-testid="blueprint-web-preview-button"
            >
              <Globe className="w-4 h-4" /> Open Web Preview
            </button>
          )}
          {onPushGitHub && (
            <button
              onClick={onPushGitHub}
              className="btn-outline-ink text-sm py-2"
              data-testid="blueprint-push-github-button"
            >
              <Github className="w-4 h-4" /> Push to GitHub
            </button>
          )}
          {onSave && (
            <button
              onClick={onSave}
              disabled={saving || saved}
              className="btn-outline-ink text-sm py-2"
              data-testid="blueprint-save-button"
            >
              <Save className="w-4 h-4" /> {saved ? "Saved to Dashboard" : saving ? "Saving..." : "Save to Dashboard"}
            </button>
          )}
          {onShare && (
            <button onClick={onShare} className="btn-outline-ink text-sm py-2" data-testid="blueprint-share-button">
              <Share2 className="w-4 h-4" /> Share preview link
            </button>
          )}
          {onBranch && (
            <button onClick={onBranch} className="btn-outline-ink text-sm py-2" data-testid="blueprint-branch-button">
              <GitBranch className="w-4 h-4" /> Branch this blueprint
            </button>
          )}
        </div>
      </header>

      <div className="grid md:grid-cols-2 gap-5">
        <Section icon={<Layers className="w-4 h-4" />} title="Screens" testid="bp-screens">
          <ul className="space-y-2.5">
            {bp.screens?.map((s, i) => (
              <li key={i} className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-saffron/20 text-marigold text-xs font-semibold flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                <div>
                  <div className="font-medium">{s.name}</div>
                  <div className="text-sm text-ink2">{s.purpose}</div>
                </div>
              </li>
            ))}
          </ul>
        </Section>

        <Section icon={<Workflow className="w-4 h-4" />} title="Navigation Flow" testid="bp-nav">
          <ol className="space-y-1.5 text-sm text-ink2">
            {bp.navigation?.map((n, i) => (
              <li key={i} className="flex gap-2">
                <span className="text-marigold">→</span><span>{n}</span>
              </li>
            ))}
          </ol>
        </Section>

        <Section icon={<Sparkles className="w-4 h-4" />} title="Components" testid="bp-components">
          <div className="flex flex-wrap gap-2">
            {bp.components?.map((c, i) => (
              <span key={i} className="px-3 py-1 rounded-full bg-paper text-ink text-xs font-medium border border-border">
                {c}
              </span>
            ))}
          </div>
        </Section>

        <Section icon={<Database className="w-4 h-4" />} title="Database Schema" testid="bp-db">
          <div className="space-y-3">
            {bp.database_schema?.map((e, i) => (
              <div key={i} className="bg-paper rounded-xl p-3 border border-border">
                <div className="font-mono text-sm font-semibold text-ink">{e.entity}</div>
                <div className="mt-1 text-xs font-mono text-ink2 space-y-0.5">
                  {e.fields?.map((f, j) => <div key={j}>· {f}</div>)}
                </div>
              </div>
            ))}
          </div>
        </Section>
      </div>

      <Section icon={<Store className="w-4 h-4" />} title="App Store Description" testid="bp-store">
        <p className="text-ink2 italic leading-relaxed">"{bp.app_store_description}"</p>
      </Section>

      {bp.backend_plan && (bp.backend_plan.endpoints?.length > 0 || bp.backend_plan.integrations?.length > 0) && (
        <div className="card-soft p-6" data-testid="bp-backend-plan">
          <div className="flex items-center gap-2 mb-4 text-marigold">
            <Server className="w-4 h-4" />
            <h3 className="text-sm font-semibold uppercase tracking-wider text-ink">Backend plan</h3>
            <span className="text-xs text-ink2 ml-2 font-normal normal-case tracking-normal">{bp.backend_plan.stack || "FastAPI + MongoDB"}</span>
          </div>

          {bp.backend_plan.endpoints?.length > 0 && (
            <div className="mb-5">
              <div className="label-eyebrow mb-2">Endpoints</div>
              <ul className="space-y-1.5 text-sm">
                {bp.backend_plan.endpoints.map((e, i) => (
                  <li key={i} className="flex gap-3 items-start">
                    <span className={`font-mono text-[10px] font-bold px-2 py-0.5 rounded ${methodTone(e.method)}`}>
                      {e.method || "GET"}
                    </span>
                    <code className="font-mono text-ink">{e.path}</code>
                    <span className="text-ink2">— {e.purpose}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {bp.backend_plan.collections?.length > 0 && (
            <div className="mb-5">
              <div className="label-eyebrow mb-2">MongoDB collections</div>
              <div className="flex flex-wrap gap-2">
                {bp.backend_plan.collections.map((c, i) => (
                  <span key={i} className="px-3 py-1 rounded-lg bg-paper border border-border text-sm font-mono">{c.name}</span>
                ))}
              </div>
            </div>
          )}

          {bp.backend_plan.integrations?.length > 0 && (
            <div className="mb-5">
              <div className="label-eyebrow mb-2 inline-flex items-center gap-2"><Plug className="w-3.5 h-3.5" /> Suggested Emergent integrations</div>
              <div className="grid sm:grid-cols-2 gap-2">
                {bp.backend_plan.integrations.map((i, idx) => (
                  <div key={idx} className="bg-paper rounded-xl p-3 border border-border">
                    <div className="font-semibold text-sm">{i.name}</div>
                    <div className="text-xs text-ink2 mt-0.5">{i.use}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {bp.backend_plan.auth && (
            <div className="text-sm text-ink2"><span className="label-eyebrow mr-2">Auth</span>{bp.backend_plan.auth}</div>
          )}
        </div>
      )}

      <div>
        <div className="label-eyebrow mb-3 flex items-center gap-2"><span>Starter SwiftUI Code</span></div>
        <CodeBlock code={bp.starter_code || ""} filename={`${(bp.title || "App").replace(/\s+/g, "")}View.swift`} />
      </div>

      <NextSteps bp={bp} onExport={onExport} onWebPreview={onWebPreview} onPushGitHub={onPushGitHub} onSave={onSave} saved={saved} />
    </article>
  );
}

function NextSteps({ bp, onExport, onWebPreview, onPushGitHub, onSave, saved }) {
  const steps = [
    onExport && {
      n: 1,
      icon: <Download className="w-4 h-4" />,
      title: "Download the starter kit",
      desc: "Multi-platform Xcode project + FastAPI backend + GitHub Actions workflow. Open in Xcode, hit Run, ship.",
      action: "Download .zip",
      onClick: onExport,
      tone: "bg-saffron/15 text-marigold",
      testid: "next-step-download",
    },
    onPushGitHub && {
      n: 2,
      icon: <Github className="w-4 h-4" />,
      title: "Push to GitHub",
      desc: "Creates a brand-new repo + commits everything. The bundled Actions workflow auto-builds your .dmg / .ipa on macos-14 runners.",
      action: "Create repo & push",
      onClick: onPushGitHub,
      tone: "bg-ink/10 text-ink",
      testid: "next-step-push",
    },
    onWebPreview && {
      n: 3,
      icon: <Globe className="w-4 h-4" />,
      title: "Open the Web Demo",
      desc: "Sandboxed clickable HTML clone of your app you can share before any code is shipped.",
      action: "Open Web Preview",
      onClick: onWebPreview,
      tone: "bg-teal/15 text-teal",
      testid: "next-step-web-preview",
    },
    onSave && !saved && {
      n: 4,
      icon: <Save className="w-4 h-4" />,
      title: "Save to your dashboard",
      desc: "Keep this blueprint, share it to the App Atlas, generate App Store screenshots, or pick it up next session.",
      action: "Save blueprint",
      onClick: onSave,
      tone: "bg-marigold/15 text-marigold",
      testid: "next-step-save",
    },
  ].filter(Boolean);

  if (steps.length === 0) return null;

  return (
    <div className="card-soft p-6 md:p-7 bg-gradient-to-br from-cream via-paper to-saffron/10" data-testid="bp-next-steps">
      <div className="flex items-center gap-2 mb-5 text-marigold">
        <Rocket className="w-5 h-5" />
        <h3 className="text-sm font-semibold uppercase tracking-wider text-ink">Next steps</h3>
        <span className="text-xs text-ink2 ml-2 font-normal normal-case tracking-normal">From prompt to App Store, in order.</span>
      </div>
      <ol className="space-y-3">
        {steps.map((s) => (
          <li key={s.testid} className="flex flex-wrap items-start gap-3 group">
            <span className={`w-9 h-9 rounded-full ${s.tone} flex items-center justify-center font-mono text-sm font-bold flex-shrink-0`}>
              {s.n}
            </span>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-ink inline-flex items-center gap-2">
                {s.icon} {s.title}
              </div>
              <div className="text-sm text-ink2 leading-relaxed">{s.desc}</div>
            </div>
            <button
              onClick={s.onClick}
              data-testid={s.testid}
              className="text-sm inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border-2 border-ink text-ink hover:bg-ink hover:text-cream transition flex-shrink-0"
            >
              {s.action} <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </li>
        ))}
      </ol>
      <div className="mt-5 pt-4 border-t border-border text-xs text-ink2 inline-flex items-center gap-2">
        <ImageIcon className="w-3.5 h-3.5" />
        Need App Store marketing screenshots? Toggle the marketing add-on in the export dialog.
      </div>
    </div>
  );
}

function Section({ icon, title, testid, children }) {
  return (
    <section className="card-soft p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3 text-marigold">
        {icon}
        <h3 className="text-sm font-semibold uppercase tracking-wider text-ink">{title}</h3>
      </div>
      {children}
    </section>
  );
}
